import React from 'react';
import styled, { css } from 'styled-components';

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'circle' | 'rounded' | 'square';
  status?: 'online' | 'offline' | 'busy' | 'away';
  className?: string;
}

const getSizeStyles = (size: AvatarProps['size']): any => {
  const sizes = {
    xs: css`
      width: 24px;
      height: 24px;
      font-size: ${({ theme }) => theme.typography.fontSize.xs};
    `,
    sm: css`
      width: 32px;
      height: 32px;
      font-size: ${({ theme }) => theme.typography.fontSize.sm};
    `,
    md: css`
      width: 40px;
      height: 40px;
      font-size: ${({ theme }) => theme.typography.fontSize.base};
    `,
    lg: css`
      width: 56px;
      height: 56px;
      font-size: ${({ theme }) => theme.typography.fontSize.lg};
    `,
    xl: css`
      width: 72px;
      height: 72px;
      font-size: ${({ theme }) => theme.typography.fontSize.xl};
    `,
  };

  return sizes[size || 'md'];
};

const getVariantStyles = (variant: AvatarProps['variant']): any => {
  const variants = {
    circle: css`
      border-radius: 50%;
    `,
    rounded: css`
      border-radius: ${({ theme }) => theme.borderRadius.lg};
    `,
    square: css`
      border-radius: 0;
    `,
  };

  return variants[variant || 'circle'];
};

const AvatarContainer = styled.div<{ size?: AvatarProps['size'] }>`
  position: relative;
  display: inline-flex;
  ${({ size }) => getSizeStyles(size)}
`;

const StyledAvatar = styled.div<AvatarProps>`
  ${({ size }) => getSizeStyles(size)}
  ${({ variant }) => getVariantStyles(variant)}
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primary.main};
  color: ${({ theme }) => theme.colors.primary.contrastText};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  overflow: hidden;
  user-select: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StatusDot = styled.span<{
  status?: AvatarProps['status'];
  size?: AvatarProps['size'];
}>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${({ size }) => {
    const dotSizes = {
      xs: '8px',
      sm: '10px',
      md: '12px',
      lg: '14px',
      xl: '16px',
    };
    return dotSizes[size || 'md'];
  }};
  height: ${({ size }) => {
    const dotSizes = {
      xs: '8px',
      sm: '10px',
      md: '12px',
      lg: '14px',
      xl: '16px',
    };
    return dotSizes[size || 'md'];
  }};
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.background.paper};
  background-color: ${({ status, theme }) => {
    const statusColors = {
      online: theme.colors.success.main,
      offline: theme.colors.neutral[600],
      busy: theme.colors.error.main,
      away: theme.colors.warning.main,
    };
    return statusColors[status || 'offline'];
  }};
`;

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'md',
  variant = 'circle',
  status,
  className,
}) => {
  return (
    <AvatarContainer size={size} className={className}>
      <StyledAvatar size={size} variant={variant}>
        {src ? (
          <img src={src} alt={alt || name || 'Avatar'} />
        ) : name ? (
          <span>{getInitials(name)}</span>
        ) : (
          <span>?</span>
        )}
      </StyledAvatar>
      {status && <StatusDot status={status} size={size} />}
    </AvatarContainer>
  );
};

export default Avatar;
